import { useMemo } from "react";
import { useSelector } from "react-redux";
import { selectActiveFilterConfiguration } from "../../../../Search/redux/searchSelectors";
import { useFormContext, useWatch } from "react-hook-form";
import { DesktopTechFilterCategory } from "./DesktopTechFilterCategory";
import { MobileTechFilterCategory } from "./MobileTechFilterCategory";
import { isNonNull, toMapFn } from "../../../../utils/reducers";
import { ReduxImperialToggle } from "~/feature/UnitMode/UnitModeToggle";
import useMediaQuery from "~/common/hooks/useMediaQuery";
import { MEDIA_QUERY_MIN_DESKTOP_S } from "~/utils/screenSizes";
import { useUnitMode } from "~/feature/UnitMode/useUnitMode";
import { useTranslation } from "react-i18next";
import { Button } from "~/common/components/Button/Button";
import { resolveResourceURl } from "~/utils/url/urlResolver";
import { Alert } from "~/common/m3/Alert/Alert";
import { isTechValueFilterCategory } from "~/Search/filterConfigModels";
import { UnitMode } from "~/feature/UnitMode/UnitMode";

const STATIC_IMAGE = <img alt="" src={resolveResourceURl("/images/dialog_all.gif")} className="w-100" />;

const TECH_FILTERS_ROWS = [
  {
    title: "page.selectionAssistant.techFilter.dimensions",
    items: {
      [UnitMode.Metric]: ["borediameter", "outsidediameter", "width"],
      [UnitMode.Imperial]: ["borediameterimperial", "outsidediameterimperial", "widthimperial"],
    },
  },
  {
    title: "page.selectionAssistant.techFilter.load_rating_temperature_limiting_speed",
    items: {
      [UnitMode.Metric]: ["basicdynamicloadratingradial", "basicstaticloadratingradial", "tempmin"],
      [UnitMode.Imperial]: [
        "basicdynamicloadratingradialimperial",
        "basicstaticloadratingradialimperial",
        "tempminimperial",
      ],
    },
  },
];

export const DesktopSATechValuesFilter = () => {
  const { t } = useTranslation();
  const unitMode = useUnitMode();
  const techFiltersByCode = useTechFiltersByCode();

  const techValueFilters = Object.values(techFiltersByCode);

  const titles = techValueFilters.map((item) => item.title);
  const { setValue, control, formState } = useFormContext();
  const allValues = useWatch({ control });

  const isClearEnabled = titles.some((title) => {
    // We need to find items belonging to this title?
    // Actually titles here are category titles.
    // But we need to check values for items in these categories.
    // techValueFilters is a list of ALL items (flattened).
    // So we can check if any of them has a value.
    return techValueFilters.some(
      (item) => allValues[`${item.uid}.min`] !== undefined || allValues[`${item.uid}.max`] !== undefined,
    );
  });

  const isDesktop = useMediaQuery(MEDIA_QUERY_MIN_DESKTOP_S);
  const isApplyEnabled = formState.isDirty;

  const onClearClick = () => {
    techValueFilters.forEach((item) => {
      setValue(`${item.uid}.min`, undefined, { shouldDirty: true, shouldTouch: true });
      setValue(`${item.uid}.max`, undefined, { shouldDirty: true, shouldTouch: true });
    });
  };

  if (!Object.values(techFiltersByCode).length) {
    return null;
  }

  return (
    <div>
      {isDesktop && <ReduxImperialToggle className={"mt-30 mb-30"} page={"SA"} />}
      <div className="scha__sa__tech-filter-section">
        {TECH_FILTERS_ROWS.map((rowConfig, index) => (
          <DesktopTechFilterCategory
            items={rowConfig.items[unitMode].map((code) => techFiltersByCode[code])}
            image={index === 0 ? STATIC_IMAGE : undefined}
            title={t(rowConfig.title)}
          />
        ))}
        <Alert severity={"info"} className={"mb-3"}>
          {t("page.selectionAssistant.dimensions.blueMessage")}
        </Alert>
        <div className={"scha__sa__tech-filter-section__buttons"}>
          <Button
            disabled={!isClearEnabled}
            onClick={onClearClick}
            variant={"text"}
            data-test-id={"techvaluefilter-clear-button"}
          >
            {t("searchPage.refinement.button.clear")}
          </Button>
          <Button
            disabled={!isApplyEnabled}
            type={"submit"}
            className="ml-30"
            data-test-id={"techvaluefilter-submit-button"}
          >
            {t("searchPage.refinement.button.apply")}
          </Button>
        </div>
      </div>
    </div>
  );
};

interface MobileSATechValuesFilterProps {
  index: number;
}

export const MobileSATechValuesFilter = ({ index }: MobileSATechValuesFilterProps) => {
  const unitMode = useUnitMode();
  const techFiltersByCode = useTechFiltersByCode();

  const items = TECH_FILTERS_ROWS[index].items[unitMode].map((code) => techFiltersByCode[code]).filter(isNonNull);

  if (!items.length) {
    return null;
  }

  const image = index === 0 ? STATIC_IMAGE : null;

  return (
    <div>
      <MobileTechFilterCategory key={index} items={items} image={image} />
    </div>
  );
};

function useTechFiltersByCode() {
  const filterConfig = useSelector(selectActiveFilterConfiguration);
  const techFiltersByCode = useMemo(() => {
    const techValueFilters = filterConfig?.filter(isTechValueFilterCategory) ?? [];
    return toMapFn(
      techValueFilters.flatMap((category) => category.items),
      (item) => item.uid,
    );
  }, [filterConfig]);

  return techFiltersByCode;
}
